import { useMemo } from 'react'

// Study twin of workspaceReplay2/hooks/useReplayState — reconstructs everything the
// participant saw in the /study IDE at time T from the telemetry stream alone.
//
// Two derivations have no direct event and are inferred; both are marked below.
// Everything else is a literal replay of a recorded payload.

// The console shows "Running…" from the RUN click until the runner replies. A run
// that prints nothing emits neither TERMINAL_OUTPUT nor TERMINAL_ERROR, so there is
// no completion event to key off. This bounds the spinner at the runner's own
// ceiling rather than leaving it spinning for the rest of the session.
const RUN_GRACE_MS = 15000

const GREETING = "Hi, I'm Cosmo. Ask me anything about this task — I'll help you reason through it, but I won't hand you the full solution."

// Pure so it can be exercised without a React tree (and reused for thumbnails).
export function deriveStudyReplayState (events, currentTime, startTime, starterCode = '') {
  const targetTime = startTime + currentTime
  const visible = events.filter(e => e.timestamp <= targetTime)
  const lastOf = (pred) => {
    for (let i = visible.length - 1; i >= 0; i--) if (pred(visible[i])) return visible[i]
    return null
  }

  // ── Cursor ──────────────────────────────────────────────────────────────────
  const lastPointer = lastOf(e => e.type === 'MOUSE_MOVE' || e.type === 'MOUSE_CLICK')
  const mousePos = lastPointer?.payload
    ? { x: lastPointer.payload.x, y: lastPointer.payload.y }
    : { x: 0.5, y: 0.5 }
  const lastClick = lastOf(e => e.type === 'MOUSE_CLICK')
  const isClicking = lastClick ? (targetTime - lastClick.timestamp) < 200 : false

  // ── Editor ──────────────────────────────────────────────────────────────────
  const lastCode = lastOf(e => e.type?.startsWith('CODE_') && e.payload?.code !== undefined)
  const code = lastCode?.payload?.code ?? starterCode

  // ── Which left panel was showing (INFERRED) ─────────────────────────────────
  // The DESC ⇄ COSMO tab flip is not itself an event. But the two panels never
  // coexist, so a pointer sample carrying region CHAT_WINDOW proves Cosmo was up
  // and TASK_DESCRIPTION proves it was not — and any chat event proves it too.
  // Take whichever signal is most recent; default to DESC (the mount default).
  const lastRegion = lastOf(e =>
    (e.type === 'MOUSE_MOVE' || e.type === 'MOUSE_CLICK') &&
    (e.payload?.region === 'CHAT_WINDOW' || e.payload?.region === 'TASK_DESCRIPTION'))
  const lastChatAny = lastOf(e => e.type?.startsWith('CHAT_'))

  let leftTab = 'desc'
  const regionAt = lastRegion?.timestamp ?? -1
  const chatAt = lastChatAny?.timestamp ?? -1
  if (chatAt > regionAt) leftTab = 'cosmo'
  else if (regionAt >= 0) leftTab = lastRegion.payload.region === 'CHAT_WINDOW' ? 'cosmo' : 'desc'

  // ── Console ─────────────────────────────────────────────────────────────────
  const termEvents = visible.filter(e =>
    e.type === 'TERMINAL_RUN' || e.type === 'TERMINAL_OUTPUT' || e.type === 'TERMINAL_ERROR')
  const lastRunIdx = termEvents.map(e => e.type).lastIndexOf('TERMINAL_RUN')

  // The app switches to OUTPUT on the first RUN and never switches back on its own.
  const consoleTab = lastRunIdx !== -1 ? 'output' : 'terminal'

  let output = ''
  let isRunning = false
  if (lastRunIdx !== -1) {
    const after = termEvents.slice(lastRunIdx + 1)
    if (!after.length) {
      isRunning = (targetTime - termEvents[lastRunIdx].timestamp) < RUN_GRACE_MS
      output = isRunning ? 'Running…' : 'Code executed (no output).'
    } else {
      output = after.map(e => e.type === 'TERMINAL_OUTPUT'
        ? (e.payload?.output ?? '')
        : `${e.payload?.type || 'Error'}: ${e.payload?.message || ''}`
      ).join('\n')
    }
  }
  const runCount = termEvents.filter(e => e.type === 'TERMINAL_RUN').length

  // ── Cosmo transcript ────────────────────────────────────────────────────────
  // The panel always opens with the seeded greeting, so replay opens with it too.
  const messages = [{ role: 'assistant', text: GREETING }]
  for (const e of visible) {
    if (e.type === 'CHAT_QUERY') messages.push({ role: 'user', text: e.payload?.text || '', at: e.timestamp })
    else if (e.type === 'CHAT_RESPONSE') messages.push({ role: 'assistant', text: e.payload?.content || '', at: e.timestamp })
  }
  const lastQuery = lastOf(e => e.type === 'CHAT_QUERY')
  const lastResponse = lastOf(e => e.type === 'CHAT_RESPONSE')
  const isAiProcessing = !!lastQuery && (!lastResponse || lastResponse.timestamp < lastQuery.timestamp)

  // Half-typed input: CHAT_TYPE carries the full field value, so the last one
  // since the message was sent is exactly what was sitting in the box.
  const lastTyped = lastOf(e => e.type === 'CHAT_TYPE')
  const chatInput = lastTyped && (!lastQuery || lastTyped.timestamp > lastQuery.timestamp)
    ? (lastTyped.payload?.text || '')
    : ''

  // ── Layout, replayed from the participant's own drags ───────────────────────
  const lastLeftResize = lastOf(e => e.type === 'PANEL_RESIZE' && e.payload?.panel === 'left')
  const lastTermResize = lastOf(e => e.type === 'PANEL_RESIZE' && e.payload?.panel === 'terminal')

  // ── Attention + outcome ─────────────────────────────────────────────────────
  const lastTab = lastOf(e => e.type === 'TAB_STATE')
  const tabVisible = lastTab ? !!lastTab.payload?.visible : true
  const sessionStart = visible.find(e => e.type === 'SESSION_START')
  const outcome = lastOf(e => e.type === 'TASK_COMPLETED' || e.type === 'TASK_TIMEOUT')

  // ── Highlight activity, for the log's benefit ───────────────────────────────
  const lastSelection = lastOf(e => e.type?.includes('_SELECT'))

  return {
    mousePos,
    isClicking,
    code,
    leftTab,
    consoleTab,
    output,
    isRunning,
    runCount,
    messages,
    chatInput,
    isAiProcessing,
    leftWidth: lastLeftResize?.payload?.newSize ?? null,
    terminalHeight: lastTermResize?.payload?.newSize ?? null,
    tabVisible,
    viewport: sessionStart?.payload
      ? { w: sessionStart.payload.window_width, h: sessionStart.payload.window_height }
      : null,
    outcome: outcome ? outcome.type : null,
    lastSelection: lastSelection?.payload?.selected_text || '',
    visibleEvents: visible
  }
}

export function useStudyReplayState (events, currentTime, startTime, starterCode = '') {
  return useMemo(
    () => deriveStudyReplayState(events, currentTime, startTime, starterCode),
    [events, currentTime, startTime, starterCode]
  )
}
